import React, { useState } from "react";
import { Link } from "gatsby";
import EncordLogoLightMode from "../../../assets/icons/EncordLogoLightMode.svg";
import "../../../styles/components/Slices/heroMondrian.scss";
import * as Yup from "yup";
import {
  CONSENT_AGREEMENT,
  FIRST_NAME_VALIDATION,
  isDomainBlocked,
  isEducationalEmail,
  LAST_NAME_VALIDATION,
  USE_CASE_TEXT_VALIDATION,
  WORK_EMAIL_VALIDATION,
  hasUTMConsent,
  createLegalConsentOptions,
} from "../../../utilities/helpers";
import { Field, Form, Formik } from "formik";
import { consentText, emailRegex, HUBSPOT_PROXY_URL } from "../../../constants";
import axios from "axios";
import { trackFormSubmission } from "../../../utilities/formEventTracking";
import xss from "xss";
import { handleHubSpotError } from "../../../utilities/hubspotErrorHandler";
import { toast } from "react-hot-toast";

const RequestTrialSchema = Yup.object().shape({
  firstname: FIRST_NAME_VALIDATION,
  lastname: LAST_NAME_VALIDATION,
  workEmail: WORK_EMAIL_VALIDATION,
  useCaseFreeTextForm: USE_CASE_TEXT_VALIDATION,
  consent_to_communicate_checkbox: CONSENT_AGREEMENT,
});

const HeroMondrian = ({ slice, location }) => {
  const isTryItFree = location?.pathname === "/demo/";
  const topSpacing = slice?.primary?.top_spacing;
  const bottomSpacing = slice?.primary?.bottom_spacing;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    const formId = "b1f2fd0e-9b34-4a84-bf8d-c2ad4b28fd3e";

    const [cookie] = document.cookie
      .split("; ")
      .filter((x) => x.includes("hubspotutk="));
    const [, hubspotutk] = cookie?.split("=") || ["", ""];
    setError("");
    try {
      if (
        !isDomainBlocked(values?.workEmail) &&
        emailRegex.test(values?.workEmail)
      ) {
        setLoading(true);

        const hasConsent = values?.consent_to_communicate_checkbox;
        const requestPayload = {
          fields: [
            {
              objectTypeId: "0-1",
              name: "firstname",
              value: values?.firstname,
            },
            {
              objectTypeId: "0-1",
              name: "lastname",
              value: values?.lastname,
            },
            {
              objectTypeId: "0-1",
              name: "email",
              value: values?.workEmail,
            },
            {
              objectTypeId: "0-1",
              name: "use_case_free_text_form",
              value: values?.useCaseFreeTextForm,
            },
            {
              objectTypeId: "0-1",
              name: "0-2/description",
              value: values["0-2/description"],
            },
            {
              objectTypeId: "0-1",
              name: "consent_to_receive_communication_checkbox",
              value: values?.consent_to_communicate_checkbox,
            },
          ].filter((x) => x.value),
          ...createLegalConsentOptions(hasConsent),
          context: {
            pageUri: location?.href,
            pageName: location?.pathname,
          },
        };

        let utmParams = {};
        if (hasUTMConsent()) {
          const urlParams = new URLSearchParams(window.location.search);
          utmParams = {
            utm_medium: urlParams.get("utm_medium") || "",
            utm_source: urlParams.get("utm_source") || "",
            utm_campaign: urlParams.get("utm_campaign") || "",
            utm_content: urlParams.get("utm_content") || "",
            utm_term: urlParams.get("utm_term") || "",
          };
          requestPayload.fields.push(
            ...Object.entries(utmParams).map(([key, value]) => ({
              objectTypeId: "0-1",
              name: key,
              value: value,
            }))
          );
        }

        if (hubspotutk) {
          requestPayload.context.hutk = hubspotutk;
        }

        await axios.post(
          HUBSPOT_PROXY_URL,
          Object.assign(requestPayload, {
            portalId: "25381551",
            formId,
          })
        );

        // Track form submission using utility function
        trackFormSubmission(formId, values?.workEmail);

        /**
         * Append custom properties to Heap
         */
        if (window.heap && hasUTMConsent()) {
          window.heap.addUserProperties({
            marketing_email: values?.workEmail,
            use_case: values?.useCaseFreeTextForm,
          });
        }
        resetForm();
        if (!isEducationalEmail(values?.workEmail)) {
          const _res = await window.hero.submit({
            email: values?.workEmail,
            use_case: values?.useCaseFreeTextForm,
          });
          await window.hero.dialog.open(_res);
        } else {
          toast.success("Thanks for submitting the form.");
        }
      } else {
        setError("Please enter your work e-mail.");
      }
    } catch (error) {
      const errorMessage = handleHubSpotError(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const modifiedLegalNotice = consentText.legalNotice.replace(
    "submit",
    "Request a demo"
  );
  
  return (
    <section className={`relative px-4 lg:px-0 bg-[#F3F2F9]`}>
      <article className={` lg:mx-11 ${isTryItFree ? "pt-20" : "pt-7.5"} `}>
        {/* <article className="relative max-w-6xl xl:px-0 xl:mx-auto sm:mx-6">
          <Link to="/">
            <EncordLogoLightMode />
          </Link>
        </article> */}
      </article>
      <article
        className={` lg:mx-11 ${topSpacing === "80" ? "sm:pt-20 pt-8" : topSpacing === "40" ? "sm:pt-10 pt-8" : topSpacing === "0" ? "pt-0" : "pt-10 lg:pt-20"}  ${bottomSpacing === "80" ? "sm:pb-20 pb-8" : bottomSpacing === "40" ? "sm:pb-10 pb-8" : bottomSpacing === "0" ? "pb-0" : "pb-10 lg:pb-20"} `}
      >
        <article className="relative max-w-6xl xl:px-0 xl:mx-auto sm:mx-6">
          <section className="flex flex-col lg:flex-row gap-16 flex-1">
            {/* Content Section */}
            <div className="flex flex-col justify-between w-full lg:flex-[0.7]">
              <h1
                role="heading"
                className={`text-3.25xl sm:text-4.5xl leading-10 m-0 max-w-full text-center sm:text-left sm:leading-[54px] sm:tracking-tightest_4 font-manrope text-purple-1100 strong_color_style font-semibold `}
                dangerouslySetInnerHTML={{
                  __html: xss(slice?.primary?.hero_title?.html),
                }}
              />

              {/* Center Content List */}
              <div className="flex flex-col gap-2 mt-6 lg:pr-7 lg:pb-2.5 sm:my-10">
                <p
                  className="text-gray-2500 font-inter font-medium text-base sm:text-lg -tracking-widest_3"
                  dangerouslySetInnerHTML={{
                    __html: xss(slice?.primary?.title?.html),
                  }}
                />
                <div className="flex flex-col gap-3.5 mt-4">
                  {slice?.items.map((item, index) => (
                    <div
                      className="flex items-center gap-3 flex-shrink-0"
                      key={index}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 size-6 aspect-square"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2.37988C17.3848 2.37988 21.75 6.74511 21.75 12.1299C21.75 17.5147 17.3848 21.8799 12 21.8799C6.61522 21.8799 2.25 17.5147 2.25 12.1299C2.25 6.74511 6.61522 2.37988 12 2.37988ZM15.7507 9.12988C15.6522 9.12988 15.5539 9.14927 15.4629 9.18701C15.372 9.22474 15.2893 9.27998 15.2197 9.34961L10.5007 14.0701L8.78101 12.3496C8.64036 12.209 8.44958 12.13 8.25073 12.1299C8.05175 12.1299 7.86045 12.2089 7.71973 12.3496C7.579 12.4903 7.5 12.6816 7.5 12.8806C7.50004 13.0795 7.57912 13.2702 7.71973 13.4109L9.96973 15.6609C10.0393 15.7306 10.1219 15.7865 10.2129 15.8242C10.3039 15.862 10.4022 15.8813 10.5007 15.8813C10.5992 15.8813 10.6969 15.8619 10.7878 15.8242C10.8789 15.7865 10.9614 15.7306 11.031 15.6609L16.281 10.4109C16.3507 10.3413 16.4059 10.2587 16.4436 10.1677C16.4813 10.0767 16.5007 9.97913 16.5007 9.88062C16.5007 9.7821 16.4813 9.68452 16.4436 9.59351C16.4059 9.50246 16.3507 9.41926 16.281 9.34961C16.2114 9.27999 16.1288 9.22473 16.0378 9.18701C15.9469 9.14931 15.8492 9.12992 15.7507 9.12988Z"
                          fill="#5E44FF"
                        />
                      </svg>
                      <p
                        className="text-gray-2700 font-inter font-medium text-base sm:text-lg -tracking-widest_3"
                        dangerouslySetInnerHTML={{
                          __html: xss(item?.description_text?.html) ,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Bottom Content */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-2 lg:gap-6 justify-between mt-8 flex-wrap ">
                <div className="flex flex-col gap-2 ">
                  <img
                    src="https://encord.cdn.prismic.io/encord/aCrmmidWJ-7kSRYA_Frame1000004792.svg"
                    alt="G2 Review Logo"
                    className="w-40 h-10"
                  />
                  <Link
                    target="_blank"
                    className="underline text-gray-600 font-inter text-xs -tracking-[0.3px]"
                    to={slice?.primary?.g2_review_link?.url}
                  >
                    Read our G2 reviews
                  </Link>
                </div>
                <div className="flex gap-2 md:justify-center sm:justify-end">
                  <img
                    className="w-12 sm:w-14 h-auto"
                    src={
                      "https://encord.cdn.prismic.io/encord/Z_Ork3dAxsiBwY-i_DataLabeling_BestSupport_QualityOfSupport.svg"
                    }
                    alt="DataLabeling_BestSupport"
                    loading="lazy"
                    width={0}
                    height={0}
                  />
                  <img
                    className="w-12 sm:w-14 h-auto"
                    src={
                      "https://encord.cdn.prismic.io/encord/Z_OrkndAxsiBwY-h_DataLabeling_EasiestToUse_EaseOfUse.svg"
                    }
                    alt="DataLabeling_EasiestToUse"
                    loading="lazy"
                    width={0}
                    height={0}
                  />
                  <img
                    className="w-12 sm:w-14 h-auto"
                    src={
                      "https://encord.cdn.prismic.io/encord/Z_OrkXdAxsiBwY-f_DataLabeling_MomentumLeader_Leader.svg"
                    }
                    alt="DataLabeling_MomentumLeader"
                    loading="lazy"
                    width={0}
                    height={0}
                  />
                  <img
                    className="w-12 sm:w-14 h-auto"
                    src={
                      "https://encord.cdn.prismic.io/encord/Z_OrkHdAxsiBwY-e_MLOpsPlatforms_HighPerformer_HighPerformer.svg"
                    }
                    alt="MLOpsPlatforms_HighPerformer"
                    loading="lazy"
                    width={0}
                    height={0}
                  />
                </div>
              </div>
            </div>
            {/* Form Section */}
            <div className="flex lg:flex-[0.3]">
              <section className="flex flex-col px-5 sm:px-9 py-7 lg:max-w-[482px]  w-full rounded-2.5xl form_shadow max-h-fit">
                <Formik
                  initialValues={{
                    firstname: "",
                    lastname: "",
                    workEmail: "",
                    useCaseFreeTextForm: "",
                    consent_to_communicate_checkbox: false,
                  }}
                  validationSchema={RequestTrialSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    errors,
                    touched,
                    values,
                    setTouched,
                    validateForm,
                    setFieldValue,
                    form,
                    isValid,
                    dirty,
                    ...rest
                  }) => {
                    return (
                      <Form
                        className={`${isEducationalEmail(values?.workEmail) ? "academic-form" : "commercial-form"}`}
                      >
                        <span>
                          {/* <div className="mb-4 text-sm font-normal text-purple-1100 lg:text-base lg:mb-10 md:mb-6">
                      Enter your details below to arrange a free trial:
                    </div> */}
                          <h4
                            className={`text-purple-1100 text-2xl tracking-tightest_5 font-semibold font-manrope flex items-center gap-2 mb-[18px] `}
                          >
                            <svg
                              className="hidden sm:block"
                              xmlns="http://www.w3.org/2000/svg"
                              role="presentation"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <path
                                d="M25.7555 21.8719C24.9733 21.0891 24.0594 20.4501 23.0555 19.9844C24.4712 18.8375 25.3743 17.0875 25.3743 15.125C25.3743 11.6625 22.4868 8.82188 19.0243 8.875C15.6149 8.92813 12.868 11.7063 12.868 15.125C12.868 17.0875 13.7743 18.8375 15.1868 19.9844C14.1827 20.4498 13.2687 21.0888 12.4868 21.8719C10.7805 23.5813 9.81179 25.8375 9.74929 28.2437C9.74845 28.2771 9.7543 28.3103 9.76649 28.3413C9.77868 28.3724 9.79696 28.4007 9.82026 28.4246C9.84356 28.4485 9.8714 28.4674 9.90215 28.4804C9.9329 28.4933 9.96592 28.5 9.99929 28.5H11.7493C11.8837 28.5 11.9962 28.3937 11.9993 28.2594C12.0587 26.4469 12.793 24.75 14.0837 23.4625C14.7441 22.7986 15.5297 22.2722 16.3949 21.9139C17.2601 21.5555 18.1878 21.3724 19.1243 21.375C21.0274 21.375 22.818 22.1156 24.1649 23.4625C25.4524 24.75 26.1868 26.4469 26.2493 28.2594C26.2524 28.3937 26.3649 28.5 26.4993 28.5H28.2493C28.2826 28.5 28.3157 28.4933 28.3464 28.4804C28.3772 28.4674 28.405 28.4485 28.4283 28.4246C28.4516 28.4007 28.4699 28.3724 28.4821 28.3413C28.4943 28.3103 28.5001 28.2771 28.4993 28.2437C28.4368 25.8375 27.468 23.5813 25.7555 21.8719ZM19.1243 19.125C18.0555 19.125 17.0493 18.7094 16.2962 17.9531C15.9182 17.5781 15.6196 17.1309 15.4183 16.638C15.217 16.1451 15.117 15.6167 15.1243 15.0844C15.1337 14.0594 15.543 13.0688 16.2587 12.3344C17.0087 11.5656 18.0118 11.1375 19.0837 11.125C20.143 11.1156 21.1712 11.5281 21.9274 12.2688C22.7024 13.0281 23.1274 14.0438 23.1274 15.125C23.1274 16.1938 22.7118 17.1969 21.9555 17.9531C21.5845 18.3259 21.1432 18.6214 20.6573 18.8226C20.1713 19.0237 19.6502 19.1265 19.1243 19.125ZM11.2962 15.95C11.268 15.6781 11.2524 15.4031 11.2524 15.125C11.2524 14.6281 11.2993 14.1438 11.3868 13.6719C11.4087 13.5594 11.3493 13.4438 11.2462 13.3969C10.8212 13.2063 10.4305 12.9438 10.093 12.6125C9.69535 12.2269 9.3824 11.7626 9.17418 11.2493C8.96595 10.736 8.867 10.1849 8.88366 9.63125C8.91179 8.62813 9.31491 7.675 10.018 6.95625C10.7899 6.16563 11.8274 5.73438 12.9305 5.74688C13.9274 5.75625 14.8899 6.14063 15.618 6.82188C15.8649 7.05313 16.0774 7.30938 16.2555 7.58438C16.318 7.68125 16.4399 7.72188 16.5462 7.68438C17.0962 7.49375 17.6774 7.35938 18.2743 7.29688C18.4493 7.27813 18.5493 7.09063 18.4712 6.93438C17.4555 4.925 15.3805 3.5375 12.9805 3.5C9.51491 3.44688 6.62741 6.2875 6.62741 9.74688C6.62741 11.7094 7.53053 13.4594 8.94616 14.6063C7.95241 15.0656 7.03679 15.7 6.24304 16.4938C4.53053 18.2031 3.56178 20.4594 3.49928 22.8687C3.49845 22.9021 3.5043 22.9353 3.51649 22.9663C3.52868 22.9974 3.54696 23.0257 3.57026 23.0496C3.59356 23.0735 3.6214 23.0924 3.65215 23.1054C3.68289 23.1183 3.71592 23.125 3.74928 23.125H5.50241C5.63679 23.125 5.74929 23.0187 5.75241 22.8844C5.81179 21.0719 6.54616 19.375 7.83679 18.0875C8.75554 17.1688 9.88054 16.5312 11.1087 16.2219C11.2305 16.1906 11.3118 16.075 11.2962 15.95Z"
                                fill="#1D0642"
                              />
                            </svg>
                            Schedule a demo call
                          </h4>
                          <div className="flex flex-col gap-4 sm:flex-row">
                            <div className="flex flex-col w-full lg:max-w-[200px]">
                              <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-sm sm:text-xs font-inter tracking-tighter_2 font-medium mb-1.5">
                                First name
                                <sup className="text-red-300">*</sup>
                              </label>
                              <Field
                                name="firstname"
                                placeholder="Your first name"
                                type="text"
                                className="text-gray-600 focus:!border-[#5E44FF] focus:ring-[#5E44FF] placeholder:text-[#6E6E6E] text-sm font-inter tracking-tighter_2 px-3 py-2font-normal rounded-lg  placeholder-[#6E6E6E] border-solid border-gray-2200 border"
                              />
                              {errors.firstname && touched.firstname ? (
                                <small className="text-red-300 text-xs sm:text-sm">
                                  {errors.firstname}
                                </small>
                              ) : null}
                            </div>

                            <div className="flex flex-col w-full lg:max-w-[200px]">
                              <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-sm sm:text-xs font-inter tracking-tighter_2 font-medium mb-1.5">
                                Last name
                                <sup className="text-red-300">*</sup>
                              </label>
                              <Field
                                name="lastname"
                                placeholder="Your last name"
                                type="text"
                                className="text-gray-600 focus:!border-[#5E44FF] focus:ring-[#5E44FF] placeholder:text-[#6E6E6E] text-sm font-inter tracking-tighter_2 px-3 py-2 font-normal rounded-lg placeholder-[#6E6E6E] border-solid border-gray-2200 border"
                              />
                              {errors.lastname && touched.lastname ? (
                                <small className="text-red-300 text-xs sm:text-sm">
                                  {errors.lastname}
                                </small>
                              ) : null}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2.5 sm:flex-row">
                            <div className="flex flex-col flex-1 w-full mt-4 lg:mt-2">
                              <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-sm sm:text-xs font-inter tracking-tighter_2 font-medium mb-1.5">
                                <>{"Work"}</> email
                                <sup className="text-red-300">*</sup>
                              </label>
                              <Field
                                name="workEmail"
                                placeholder="Enter your work email"
                                type="text"
                                className="text-gray-600 focus:!border-[#5E44FF] focus:ring-[#5E44FF] placeholder:text-[#6E6E6E] text-sm font-inter tracking-tighter_2 py-2.5 px-3 font-normal rounded-lg placeholder-[#6E6E6E] border-solid border-gray-2200 border"
                              />
                              {errors.workEmail && touched.workEmail ? (
                                <small className="text-red-300 text-xs sm:text-sm">
                                  {errors.workEmail}
                                </small>
                              ) : null}
                            </div>
                          </div>
                          {error && (
                            <small className="text-red-300 text-xs sm:text-sm">
                              {error}
                            </small>
                          )}
                        </span>
                        <span>
                          {isTryItFree ? (
                            <>
                              <div className="flex flex-col mt-4 lg:mt-2">
                                <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-sm sm:text-xs leading-5 font-inter font-medium mb-1.5">
                                  What's your use case?
                                  <sup className="text-red-300">*</sup>
                                </label>
                                <Field
                                  as="textarea"
                                  name="useCaseFreeTextForm"
                                  placeholder="Tell us about your use case."
                                  className="text-gray-600 h-20 sm:h-24 focus:!border-[#5E44FF] focus:ring-[#5E44FF] placeholder:text-[#6E6E6E] text-sm font-inter tracking-tighter_2 px-3 py-2 font-normal rounded-lg placeholder-[#6E6E6E] border-solid border-gray-2200 border"
                                />
                                {errors.useCaseFreeTextForm &&
                                touched.useCaseFreeTextForm ? (
                                  <small className="text-red-300 text-xs sm:text-sm">
                                    {errors.useCaseFreeTextForm}
                                  </small>
                                ) : null}
                              </div>
                              <div className="mt-4 gap-2 flex flex-col">
                                <label className="flex gap-2 text-xs consent-required-histerik text-gray-1200 font-normal items-start">
                                  <Field
                                    className="mt-[1px] appearance-none w-4 h-4 border cursor-pointer border-gray-300 checked:bg-purple-1500 checked:border-none "
                                    type="checkbox"
                                    name="consent_to_communicate_checkbox"
                                    // required
                                  />
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: consentText?.marketingConsent,
                                    }}
                                  />
                                </label>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col mt-4 lg:mt-2.5">
                                <label className="text-gray-2500 placeholder:text-[#a8a4a4] text-sm font-inter tracking-tighter_2 font-medium mb-1.5">
                                  What's your use case?
                                  <sup className="text-red-300">*</sup>
                                </label>
                                <Field
                                  as="textarea"
                                  name="0-2/description"
                                  placeholder="Tell us about your use case."
                                  className="text-gray-600 h-20 sm:h-24 focus:!border-[#5E44FF] focus:ring-[#5E44FF] placeholder:text-[#BFBFBF] text-sm font-inter tracking-tighter_2 px-3 sm:px-5 py-2 sm:py-2.5 font-normal rounded-lg placeholder-[#6E6E6E] border-solid border-gray-2200 border"
                                />
                                {errors["0-2/description"] &&
                                touched["0-2/description"] ? (
                                  <small className="text-red-300 text-xs sm:text-sm">
                                    {errors["0-2/description"]}
                                  </small>
                                ) : null}
                              </div>
                              <div className="mt-4 gap-2 flex flex-col">
                                <label className="flex gap-2 text-xs consent-required-histerik text-gray-1200 font-normal items-start">
                                  <Field
                                    className="mt-[1px] appearance-none w-4 h-4 border cursor-pointer border-gray-300 checked:bg-purple-1500 checked:border-none "
                                    type="checkbox"
                                    name="consent_to_communicate_checkbox"
                                    // required
                                  />
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: consentText?.marketingConsent,
                                    }}
                                  />
                                </label>
                              </div>
                            </>
                          )}

                          <div className="flex gap-5">
                            <button
                              type={"submit"}
                              disabled={loading || !(isValid && dirty)}
                              className={`disabled:cursor-not-allowed bg-[#5E44FF] font-semibold font-manrope tracking-tighter_3 border-[#5E44FF] text-white py-2 sm:py-2.5 text-sm sm:text-base shadow-lg rounded-full w-full mx-auto my-4 lg:mt-4 lg:mb-5 hover:border-[#5E24FF] hover:bg-[#5E24FF]`}
                              {...rest}
                            >
                              {loading ? (
                                <div className="flex justify-center w-4 h-4 sm:w-5 sm:h-5 m-auto border-2 border-white border-solid rounded-full animate-spin border-t-transparent"></div>
                              ) : (
                                <>Request a demo</>
                              )}
                            </button>
                          </div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: modifiedLegalNotice,
                            }}
                            className="text-xs text-gray-1200 font-normal consent-anchor-tag"
                          />
                        </span>
                      </Form>
                    );
                  }}
                </Formik>

                {/* <div
                  className="text-xs font-normal text-center text-gray-600 font-inter trail_anchor_link"
                  dangerouslySetInnerHTML={{
                    __html: slice?.primary?.disclaimer_text?.html,
                  }}
                ></div> */}
              </section>
            </div>
          </section>
        </article>
      </article>
    </section>
  );
};

export default HeroMondrian;
